import { Injectable } from '@angular/core';
import { ClaimsService } from './claims.service';
import { KycService } from './kyc.service';
import { Action, EnrichedAction } from '../models/action.model';
import { Kyc_Check } from '../models/kyc_check.model';

@Injectable({
  providedIn: 'root'
})
export class ActionFlowService {

  constructor(
    private cs: ClaimsService,
    private ks: KycService
  ) { }

  /**
   * Carrega e processa as actions pendentes para um claim e usuário específicos.
   * Realiza o enriquecimento de status, verificação de pré-requisitos e ordenação.
   */
  async getActionsForClaim(claimId: string, userId: string): Promise<Action[]> {
    try {
      // 1. Buscar estrutura de actions (sem status)
      const enrichedActions = await this.cs.getOutstandingActionsV3(claimId);
      
      if (!enrichedActions || enrichedActions.length === 0) {
        return [];
      }

      // 2. Preencher status de Questionários e Assinaturas
      await this.fillActionStatuses(enrichedActions, userId, claimId);
      
      // 3. Processar KYCs
      const kycs = enrichedActions.filter((e) => e.action_type === 'KYC');
      const kycChecks: (Kyc_Check | null)[] = [];

      if (kycs.length > 0) {
        // Cross-case KYC reuse (CPK-3705): fetch once before the loop — the
        // query is identical for every iteration (same userId, same filters).
        const { data: anyApproved } = await this.ks.supabase
          .from('cp_kyc_check')
          .select('id, passed, stage, created_at')
          .eq('checked_user_id', userId)
          .eq('stage', 'DONE')
          .eq('passed', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Para cada KYC action, busca o check mais recente DESTA action específica
        // (action_id da action OU action_id IS NULL para checks legados),
        // espelhando o filtro do backend em kyc-check. Assim um novo attempt de KYC
        // após assinar o legal pack reflete o status correto por action, e não o
        // check mais recente do usuário de forma global.
        for (const kyc of kycs) {
          const { data: kycCheckData } = await this.ks.supabase
            .from('cp_kyc_check')
            .select('*')
            .eq('checked_user_id', userId)
            .or(`action_id.eq.${kyc.action_id},action_id.is.null`)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          // If no action-specific check exists, fall back to any approved KYC
          // for this user so the action renders as DONE instead of pending.
          const resolvedCheck = kycCheckData ?? anyApproved;

          // Preenche done_action_id e ac_status na KYC action
          if (resolvedCheck) {
            kyc.done_action_id = resolvedCheck.id;
            kyc.ac_status = resolvedCheck.passed ? 'DONE' : resolvedCheck.stage;
            kyc.status = resolvedCheck.stage;
            kycChecks.push(resolvedCheck as any as Kyc_Check);
          } else {
            kycChecks.push(null);
          }
        }
      }
      
      // 4. Atualizar status de pré-requisitos
      await this.updatePrerequisiteStatuses(enrichedActions);
      
      // 5. Filtrar e selecionar KYC
      const finalList = enrichedActions.filter((e) => e.action_type !== 'KYC');

      if (kycs && kycs.length > 0) {
        finalList.push(kycs[this.selectKycFromList(kycChecks)]);
      }

      // 6. ✅ NÃO reordena por tipo — a ordem topológica do getOutstandingActionsV3 já respeita pré-requisitos

      // 7. Converter para Action
      return finalList.map((ea) => this.enrichedToAction(ea));

    } catch (error) {
      console.error('[ActionFlowService] Error loading actions', error);
      throw error;
    }
  }

  /**
   * Resolve o destino real de um redirect pós-login que aponta para uma
   * questionnaire específica (ex: link de outreach por email/N8N com
   * redirectTo=/client/claim/<id>/q/<questionnaireId>).
   *
   * Um link desses é gerado uma única vez e pode ser clicado de novo depois
   * que a questionnaire alvo já foi respondida (CPK-4246) — nesse caso,
   * navegar direto pro redirectTo reabre o formulário já enviado, prefillado,
   * permitindo reenvio. Aqui recalculamos a action pendente de verdade no
   * momento do clique em vez de confiar no destino estático do link.
   *
   * CPK-4246 AC: "Submitted questionnaires should not be opened by the client
   * accessing the email's link" — se não conseguirmos confirmar que a
   * questionnaire alvo ainda está pendente (erro ao carregar as actions), o
   * fallback é para /client (nunca para o link estático original), já que o
   * requisito é não reabrir uma questionnaire já enviada mesmo nesse caso.
   */
  async resolvePendingActionRedirect(
    redirectPath: string | null,
    userId: string | undefined
  ): Promise<string> {
    if (!redirectPath) {
      return '/client';
    }

    const match = redirectPath.match(/^\/client\/claim\/([^/]+)\/q\/([^/?]+)/);
    if (!match || !userId) {
      return redirectPath;
    }

    const claimId = match[1];
    const questionnaireId: string | null = match[2] ?? null;

    try {
      const actions = await this.getActionsForClaim(claimId, userId);

      const targetAction = actions.find(
        (a) => a.action_type === 'QUESTIONNAIRE' && a.questionnaire_id === questionnaireId
      );

      if (targetAction && !this.isActionComplete(targetAction) && targetAction.prstatus === 'DONE') {
        return redirectPath;
      }

      const nextPending = actions.find((a) => !this.isActionComplete(a) && a.prstatus === 'DONE');

      if (nextPending?.action_type === 'QUESTIONNAIRE' && nextPending.questionnaire_id) {
        return `/client/claim/${claimId}/q/${nextPending.questionnaire_id}`;
      }

      if (nextPending) {
        return `/client/claim/${claimId}`;
      }

      return '/client';
    } catch (error) {
      console.error('[ActionFlowService] Failed to resolve pending action redirect, falling back to /client', error);
      return '/client';
    }
  }

  /**
   * Preenche status de todas as actions (Questionários e Assinaturas)
   */
  private async fillActionStatuses(
    actions: EnrichedAction[],
    userId: string,
    claimId: string
  ): Promise<void> {
    // Separar por tipo
    const questionnaires = actions.filter(a => a.action_type === 'QUESTIONNAIRE');
    const signatures = actions.filter(a => a.action_type === 'SIGNATURE');

    // 1️⃣ Para assinaturas, buscar contratos e suas versões anteriores (attributes.previous_versions)
    const contractIdsMap = new Map<string, string[]>(); // contractId atual -> [atual, ...versões anteriores]
    
    if (signatures.length > 0) {
      const contractIds = signatures.map(s => s.contract_id!).filter(id => !!id);
      
      // Busca todos os contratos de uma vez para obter previous_versions
      const { data: contracts } = await this.cs.supabase
        .from('cp_contract')
        .select('id, attributes')
        .in('id', contractIds);
      
      // Monta mapa com versões (atual + anteriores)
      for (const contract of contracts || []) {
        const allVersions = [contract.id];
        
        if (contract.attributes?.previous_versions && Array.isArray(contract.attributes.previous_versions)) {
          allVersions.push(...contract.attributes.previous_versions);
        }
        
        contractIdsMap.set(contract.id, allVersions);
      }
      
      console.log('[ActionFlowService] Contract versions map', {
        totalContracts: contractIds.length,
        versionsMap: Array.from(contractIdsMap.entries()).map(([id, versions]) => ({
          current: id,
          versions: versions.length,
          allVersions: versions
        }))
      });
    }

    // 2️⃣ Buscar status em paralelo
    const [questionnaireStatuses, signatureStatuses] = await Promise.all([
      // Questionários
      questionnaires.length > 0
        ? this.cs.supabase
            .from('cp_questionnaire_answer')
            .select('id, questionnaire_id, status')
            .eq('claim_id', claimId)
            // .eq('responsible_user_id', userId)
            .in(
              'questionnaire_id',
              questionnaires.map(q => q.questionnaire_id!).filter(id => !!id)
            )
        : Promise.resolve({ data: [], error: null }),

      // Assinaturas - buscar por TODAS as versões dos contratos (atual + previous_versions)
      signatures.length > 0 && contractIdsMap.size > 0
        ? this.cs.supabase
            .from('cp_signed_contract')
            .select('id, contract_id, status, claim_id, signer_id, created_at')
            .eq('claim_id', claimId)
            .in(
              'contract_id',
              Array.from(new Set(
                Array.from(contractIdsMap.values()).flat()
              ))
            )
            .order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);

    // 3️⃣ Mapear questionários
    if (questionnaireStatuses.data) {
      for (const qa of questionnaireStatuses.data) {
        const action = questionnaires.find(
          a => a.questionnaire_id === qa.questionnaire_id
        );
        if (action) {
          action.done_action_id = qa.id;
          action.ac_status = qa.status;
          action.status = qa.status;
        }
      }
    }

    // 4️⃣ Mapear assinaturas verificando em TODAS as versões (atual + previous_versions)
    // Lógica:
    // 1. Se existe SIGNED em qualquer versão, usa ele (independente de haver CANCELED depois)
    // 2. Se não existe SIGNED, pega o mais recente (pode ser CANCELED/TERMINATED para mostrar status)
    if (signatureStatuses.data && signatureStatuses.data.length > 0) {
      // Para cada action, procurar assinatura em suas versões
      for (const action of signatures) {
        const versionsToCheck = contractIdsMap.get(action.contract_id!) || [action.contract_id!];
        
        // Buscar todos os contratos assinados que correspondem a qualquer versão
        const contractsForThisAction = signatureStatuses.data.filter(
          sc => sc.contract_id && versionsToCheck.includes(sc.contract_id)
        );
        
        if (contractsForThisAction.length === 0) continue;
        
        console.log('[ActionFlowService] Checking signature for action', {
          actionContractId: action.contract_id,
          versionsToCheck,
          foundContracts: contractsForThisAction.length,
          statuses: contractsForThisAction.map(c => ({
            id: c.id,
            contract_id: c.contract_id,
            status: c.status,
            created_at: c.created_at
          }))
        });
        
        // Prioridade 1: Procurar por SIGNED em qualquer versão
        const signedContract = contractsForThisAction.find(c => c.status === 'SIGNED');
        
        // Prioridade 2: Se não há SIGNED, pega o mais recente (para mostrar CANCELED/TERMINATED)
        const latestContract = contractsForThisAction[0]; // Já está ordenado por created_at DESC
        
        const selectedContract = signedContract || latestContract;
        
        action.done_action_id = selectedContract.id;
        action.ac_status = selectedContract.status;
        action.status = selectedContract.status;
        action.signer_id = selectedContract.signer_id;
        
        console.log('[ActionFlowService] Selected contract for action', {
          actionContractId: action.contract_id,
          selectedContractId: selectedContract.contract_id,
          status: selectedContract.status,
          reason: signedContract ? 'found SIGNED' : 'using latest'
        });
      }
    }
  }

  /**
   * Atualiza o prstatus das actions verificando se os pré-requisitos foram completados
   */
  private async updatePrerequisiteStatuses(
    actions: EnrichedAction[]
  ): Promise<void> {
    // Buscar pré-requisitos da tabela cp_action_prerequisites
    const caseActionIds = actions.map(a => a.case_action_id);
    const { data: prerequisites, error } = await this.cs.supabase
      .from('cp_action_prerequisites')
      .select('action_id, prereq_id')
      .in('action_id', caseActionIds)
      .in('prereq_id', caseActionIds);

    if (error) {
      console.error('[ActionFlowService] Error fetching prerequisites', error);
      return;
    }

    // Para cada action com pré-requisitos
    for (const action of actions) {
      const actionPrereqs = prerequisites?.filter(
        p => p.action_id === action.case_action_id
      ) || [];

      if (actionPrereqs.length === 0) {
        // Sem pré-requisitos, marca como DONE
        action.prstatus = 'DONE';
        continue;
      }

      // Verificar se todos os pré-requisitos estão completos
      let allPrereqsComplete = true;

      for (const prereq of actionPrereqs) {
        const prereqAction = actions.find(a => a.case_action_id === prereq.prereq_id);
        
        if (!prereqAction) {
          allPrereqsComplete = false;
          break;
        }

        // Verificar se pré-requisito está completo
        const isComplete = this.isActionComplete(prereqAction);
        
        if (!isComplete) {
          allPrereqsComplete = false;
          break;
        }
      }

      // Atualizar prstatus
      const newPrstatus = allPrereqsComplete ? 'DONE' : 'OPEN';
      action.prstatus = newPrstatus;
    }
  }

  /**
   * Verifica se uma action está completa baseado no ac_status
   */
  private isActionComplete(action: { action_type: string; ac_status: string | null }): boolean {
    switch (action.action_type) {
      case 'QUESTIONNAIRE':
        return action.ac_status === 'DONE' || action.ac_status === 'SUBMITTED';

      case 'KYC':
        return action.ac_status === 'DONE';

      case 'SIGNATURE':
        return action.ac_status === 'SIGNED';

      default:
        return false;
    }
  }

  /**
   * Converte EnrichedAction para Action
   */
  private enrichedToAction(enriched: EnrichedAction): Action {
    return {
      case_action_id: enriched.case_action_id,
      action_id: enriched.action_id,
      claim_id: enriched.claim_id,
      done_action_id: enriched.done_action_id,
      action_name: enriched.action_name,
      action_type: enriched.action_type,
      type: enriched.type,
      config: enriched.config,
      questionnaire_id: enriched.questionnaire_id,
      contract_id: enriched.contract_id,
      ac_status: enriched.ac_status,
      prstatus: enriched.prstatus,
      status: enriched.status,
      description: enriched.description,
      signer_id: enriched.signer_id,
    } as Action;
  }

  private selectKycFromList(list: (Kyc_Check | null)[]): number {
    for (let i = 0; i < list.length; i++) {
      const kyc = list[i];
      if (kyc?.passed) {
        return i;
      }
    }

    if (list.length <= 1) {
      return 0;
    }

    let nl = list.filter((e) => !!e);

    nl = nl.sort((a, b) => {
      const val =
        new Date(b!.created_at!).getTime() - new Date(a!.created_at!).getTime();
      return val;
    });

    const index = list.findIndex((v) => {
      return nl[0]!.id == v?.id;
    });

    if (index == -1) return 0;

    return index;
  }
}

